import useHasRequiredRole from 'hooks/useHasRequiredRole';
import { GUEST } from 'constants/constants';
import { Grid } from 'react-flexbox-grid';
import { useCallback, type ComponentType } from 'react';
import StepWizard from 'components/common/StepWizard';
import useFormValuesRef from 'hooks/useFormValuesRef';
import requireValue from 'utils/requireValue';
import {
  BuildHouseFirstStep,
  BuildHouseSecondStep,
  BuildHouseThirdStep,
  BuildHouseFourthStep,
  BuildHouseFifthStep,
  BuildHouseSixthStep,
} from './tabs';
import Nav from '../Nav';
import CostOverview from '../CostOverviewTab';
import type { IBuildHouseFormValues, IBuildHouseStepProps, TBuildHouseSubmit } from './types';

interface IBuildHouseTab {
  label: string;
  component: ComponentType<IBuildHouseStepProps>;
}

interface IHouseDetailsProps {
  initialValues: IBuildHouseFormValues;
  onSubmit: TBuildHouseSubmit;
  isEditMode?: boolean;
  projectType?: string;
  step?: number | null;
}

const formValueMock: IBuildHouseFormValues = {};

const tabs: IBuildHouseTab[] = [
  { label: 'project_details.tabs.house_info', component: BuildHouseFirstStep },
  { label: 'project_details.tabs.address', component: BuildHouseSecondStep },
  { label: 'project_details.tabs.energy', component: BuildHouseThirdStep },
  { label: 'project_details.tabs.indoor', component: BuildHouseFourthStep },
  { label: 'project_details.tabs.outdoor', component: BuildHouseFifthStep },
  { label: 'project_details.tabs.other', component: BuildHouseSixthStep },
  { label: 'project_details.tabs.cost_overview', component: CostOverview },
];

export default function HouseDetails({
  initialValues,
  onSubmit,
  isEditMode,
  projectType,
  step,
}: IHouseDetailsProps) {
  const isGuest = useHasRequiredRole(GUEST);
  const formValuesRef = useFormValuesRef<IBuildHouseFormValues>([], {
    formValueMock,
    initialValues,
  });

  const onSubmitStep = useCallback(
    (values: IBuildHouseFormValues) => {
      formValuesRef.current = { ...formValuesRef.current, ...values };
    },
    [formValuesRef],
  );

  const onBack = useCallback(
    (values: IBuildHouseFormValues, callback?: () => void) => {
      onSubmitStep(values);
      requireValue(callback, 'callback')();
    },
    [onSubmitStep],
  );

  return (
    <Grid>
      <StepWizard
        // StepWizard starts at the first step for null (null - 1) as for its default (1 - 1).
        initialStep={step ?? undefined}
        nav={<Nav tabs={tabs} isEditMode={isEditMode} />}
      >
        {tabs.map(({ label, component: C }) => (
          <C
            key={label}
            {...{
              isGuest,
              onSubmitStep,
              formValuesRef,
              initialValues,
              onSubmit,
              isEditMode,
              projectType,
              onBack,
            }}
          />
        ))}
      </StepWizard>
    </Grid>
  );
}
